import React, { Fragment, useState } from 'react';
import { Row, Col, Input, Form, Button, UncontrolledPopover, PopoverBody } from 'reactstrap';
import { CommonModal, ButtonBuilder, ToastBuilder, RegisteredInputsBuilder } from '../../../../../../../../../GlobalComponents/Builders';
import { ApiRequest } from '../../../../../../../../../GlobalComponents/Builders';
import { handleCopy } from '../../../../../../../../../GlobalComponents/Helpers';
import { useForm } from 'react-hook-form';
import { Copy, Plus, XCircle, ChevronRight, ChevronDown } from 'react-feather';
import { ENDPOINT_HINTS, COMPOSITE_HINTS, PARAM_HINTS, INTEGRATION_INPUT_HINTS, OPERATOR_REFERENCE } from './hints';

/**
 * ApiExplorerModal — Read-only API explorer for the source or destination connection
 * directly from the DMT Job page. Makes GET requests using connection credentials.
 */

const ApiExplorerModal = ({ jobData, trigger, connectionId: directConnectionId, serviceCode: directServiceCode, connectionName: directConnectionName }) => {

    const [modal, setModal] = useState(false);
    const [activeConnection, setActiveConnection] = useState('source');
    // Last response per connection slot, so switching connections keeps each side's
    // result for side-by-side comparison.
    const [resultsByConnection, setResultsByConnection] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [argsMode, setArgsMode] = useState('simple'); // 'simple' or 'advanced'
    const [paramRows, setParamRows] = useState([{ key: '', value: '' }]);
    const [rawArgs, setRawArgs] = useState('');
    const [rawIntegrationOptions, setRawIntegrationOptions] = useState('');
    const [activeGroup, setActiveGroup] = useState(null); // selected endpoint hint group
    // Full form snapshot per connection slot, so switching connections restores that
    // side's endpoint/params/options instead of leaking the previous connection's.
    const [formByConnection, setFormByConnection] = useState({});

    const { register, handleSubmit, reset, formState: { errors }, setValue, getValues, watch } = useForm();
    const watchedUri = watch('exploreUri');

    // Standalone mode: direct connectionId/serviceCode props (e.g., from Connection page)
    const isStandalone = !!directConnectionId;

    // Persist across close/reopen: don't reset form, params, responses, or snapshots.
    const openModal = () => setModal(true);

    const getConnectionId = () => {
        if (isStandalone) return directConnectionId;
        if (activeConnection === 'source') return jobData?.source_connection?.id;
        if (activeConnection === 'destination') return jobData?.destination_connection?.id;
        if (activeConnection === 'test') return jobData?.test_connection?.id;
        return null;
    };

    const getServiceCode = () => {
        if (isStandalone) return directServiceCode || null;
        if (activeConnection === 'source') return jobData?.source_connection?.service_code;
        if (activeConnection === 'destination') return jobData?.destination_connection?.service_code;
        if (activeConnection === 'test') return jobData?.test_connection?.service_code;
        return null;
    };

    // Which slot the current response is stored under.
    const connectionKey = () => isStandalone ? `conn:${directConnectionId}` : activeConnection;
    const results = resultsByConnection[connectionKey()] ?? null;
    const setResults = (val) => setResultsByConnection(prev => ({ ...prev, [connectionKey()]: val }));

    const EMPTY_FORM = {
        fields: { exploreUri: '', explorePageSize: '', exploreFields: '' },
        paramRows: [{ key: '', value: '' }], argsMode: 'simple', rawArgs: '',
        rawIntegrationOptions: '', activeGroup: null,
    };
    const snapshotForm = () => ({
        fields: {
            exploreUri: getValues('exploreUri') || '',
            explorePageSize: getValues('explorePageSize') || '',
            exploreFields: getValues('exploreFields') || '',
        },
        paramRows, argsMode, rawArgs, rawIntegrationOptions, activeGroup,
    });
    const applyForm = (snap) => {
        reset(snap.fields);
        setParamRows(snap.paramRows);
        setArgsMode(snap.argsMode);
        setRawArgs(snap.rawArgs);
        setRawIntegrationOptions(snap.rawIntegrationOptions);
        setActiveGroup(snap.activeGroup);
    };
    // Switch job-mode connection slots, preserving each slot's full form state.
    const switchConnection = (slot) => {
        if (slot === activeConnection) return;
        setFormByConnection(prev => {
            const saved = { ...prev, [activeConnection]: snapshotForm() };
            applyForm(saved[slot] || EMPTY_FORM);
            return saved;
        });
        setActiveConnection(slot);
    };

    // Reset the current connection's inputs + cached response; other slots untouched.
    const clearCurrentConnection = () => {
        const key = connectionKey();
        applyForm(EMPTY_FORM);
        setFormByConnection(prev => { const next = { ...prev }; delete next[key]; return next; });
        setResultsByConnection(prev => { const next = { ...prev }; delete next[key]; return next; });
    };

    // Clicking a hint merges it with the current URI: concrete values already filled
    // on shared leading segments are kept; {placeholder}s and trailing segments come
    // from the hint. So /types/9 + .../types/{typeId}/questions -> /types/9/questions,
    // and deeper IDs survive when you jump back up the chain.
    const isPlaceholder = (seg) => /^\{.*\}$/.test(seg);
    const resolveHintUri = (template) => {
        const lead = (template.match(/^\//) || [])[0] || ''; // preserve leading slash (AT)
        const cur = (getValues('exploreUri') || '').replace(/^\//, '').split('/');
        const tpl = template.replace(/^\//, '').split('/');
        const out = tpl.map((seg, i) => {
            const curSeg = cur[i];
            // Same slot is a placeholder in the template but concrete in the current URI -> keep current.
            if (isPlaceholder(seg) && curSeg && !isPlaceholder(curSeg)) return curSeg;
            return seg;
        });
        return lead + out.join('/');
    };
    const pickHint = (uri) => setValue('exploreUri', resolveHintUri(uri), { shouldDirty: true });

    const getHints = () => ENDPOINT_HINTS[getServiceCode()] || [];
    // Split endpoint hints into pinned items (bare {uri,label}, always shown) and
    // collapsible groups ({group, items}, revealed on click).
    const getHintLayout = () => {
        const hints = getHints();
        const pinned = hints.filter(h => !h.items);
        const groups = hints.filter(h => h.items);
        return { pinned, groups };
    };
    const toggleGroup = (name) => setActiveGroup(prev => prev === name ? null : name);
    const getCompositeHints = () => COMPOSITE_HINTS[getServiceCode()] || [];
    const getParamHints = () => PARAM_HINTS[getServiceCode()] || [];
    const getOperatorReference = () => OPERATOR_REFERENCE[getServiceCode()] || null;
    const getIntegrationInputHints = () => INTEGRATION_INPUT_HINTS[getServiceCode()] || [];

    // Autotask entity-info toggle: flip the URI between an entity query and its /EntityInformation/Fields metadata.
    const ENTITY_INFO_SUFFIX = '/EntityInformation/Fields';
    const isAutotask = () => getServiceCode() === 'autotask';
    const uriHasEntityInfo = (uri) => (uri || '').toLowerCase().includes('entityinformation');

    const toggleEntityInfo = () => {
        const current = (getValues('exploreUri') || '').trim();
        if (!current) {
            return ToastBuilder('error', 'Enter an entity URI first (e.g. /Companies)');
        }
        if (uriHasEntityInfo(current)) {
            // Strip the suffix (case-insensitive), back to the base entity.
            const next = current.replace(new RegExp(ENTITY_INFO_SUFFIX + '$', 'i'), '');
            setValue('exploreUri', next || '/', { shouldDirty: true });
        } else {
            setValue('exploreUri', current.replace(/\/$/, '') + ENTITY_INFO_SUFFIX, { shouldDirty: true });
        }
    };

    // Param row helpers
    const addParamRow = () => setParamRows(prev => [...prev, { key: '', value: '' }]);
    const removeParamRow = (idx) => setParamRows(prev => {
        const next = prev.filter((_, i) => i !== idx);
        return next.length ? next : [{ key: '', value: '' }]; // keep one empty row
    });
    const updateParamRow = (idx, field, val) => setParamRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));

    const buildArgsFromRows = () => {
        const args = {};
        paramRows.forEach(row => {
            if (row.key.trim()) {
                // Keep value as-is (literal string). Only parse if it starts with [ or { (explicit JSON)
                let val = row.value;
                if (val.trim().startsWith('{') || val.trim().startsWith('[')) {
                    try { val = JSON.parse(val); } catch (e) { /* keep as string */ }
                }
                args[row.key.trim()] = val;
            }
        });
        return args;
    };

    // Sync between modes
    const switchToAdvanced = () => {
        const args = buildArgsFromRows();
        if (Object.keys(args).length > 0) {
            setRawArgs(JSON.stringify(args, null, 2));
        }
        setArgsMode('advanced');
    };

    const switchToSimple = () => {
        if (rawArgs.trim()) {
            try {
                const parsed = JSON.parse(rawArgs);
                const rows = Object.entries(parsed).map(([key, value]) => ({
                    key,
                    value: typeof value === 'object' ? JSON.stringify(value) : String(value),
                }));
                if (rows.length > 0) setParamRows(rows);
            } catch (e) { /* keep current rows */ }
        }
        setArgsMode('simple');
    };

    const handleExplore = async (formData) => {
        let exploreArgs = {};

        if (argsMode === 'simple') {
            exploreArgs = buildArgsFromRows();
        } else {
            if (rawArgs.trim()) {
                try {
                    exploreArgs = JSON.parse(rawArgs);
                } catch (e) {
                    ToastBuilder('error', 'Extra Args must be valid JSON');
                    setResults(null);
                    setIsProcessing(false);
                    return;
                }
            }
        }

        const body = {
            uri: formData.exploreUri,
        };

        if (formData.explorePageSize && formData.explorePageSize.trim()) {
            const ps = parseInt(formData.explorePageSize);
            if (!isNaN(ps) && ps > 0) body.pageSize = ps;
        }

        if (formData.exploreFields && formData.exploreFields.trim()) {
            body.fields = formData.exploreFields.trim();
        }

        if (Object.keys(exploreArgs).length > 0) {
            body.args = exploreArgs;
        }

        // integrationOptions — integration-specific inputs (setupField, nestedLevel, …).
        let integrationOptions = {};
        if (rawIntegrationOptions.trim()) {
            try {
                integrationOptions = JSON.parse(rawIntegrationOptions);
            } catch (e) {
                ToastBuilder('error', 'Integration Options must be valid JSON');
                setResults(null);
                setIsProcessing(false);
                return;
            }
        }
        if (Object.keys(integrationOptions).length > 0) {
            body.integrationOptions = integrationOptions;
        }

        await sendExplore(body);
    };

    // Shared GET firing for the form submit.
    const sendExplore = async (body) => {
        const connectionId = getConnectionId();
        if (!connectionId) {
            return ToastBuilder('error', 'No connection selected');
        }

        // Pin the response to the slot it was fired from, even if the user switches mid-request.
        const key = connectionKey();
        const writeResult = (val) => setResultsByConnection(prev => ({ ...prev, [key]: val }));

        setIsProcessing(true);
        writeResult({ loading: true, data: 'Sending GET request...' });

        const res = await ApiRequest('POST', '/connections/{connectionId}/explore', {
            pathParams: { connectionId },
            body,
            setIsProcessing,
        });

        // The explore endpoint returns the upstream result in-band: a failed GET still
        // comes back as HTTP 200 with { success:false, ... }. So res.data is the source
        // of truth whenever present — ApiRequest flipping res.success to false just
        // mirrors that in-band flag; don't discard the payload on it.
        if (res.data) {
            writeResult(res.data);
            if (res.data.success) {
                ToastBuilder('success', `GET ${body.uri} — ${res.data.status}`);
            }
            // On failure the inline banner shows the error; no toast needed.
        } else {
            writeResult({ success: false, error: res.error || 'No response received.', uri: body.uri });
            ToastBuilder('error', 'API Explorer request failed');
        }
    };

    // Composite tab: configure the form (URI + integrationOptions). User still hits Send.
    const applyComposite = (hint) => {
        setValue('exploreUri', resolveHintUri(hint.uri));
        setRawIntegrationOptions(hint.integrationOptions ? JSON.stringify(hint.integrationOptions, null, 2) : '');
    };

    const getResultString = () => {
        if (!results || !results.data) return '';
        return typeof results.data === 'object' ? JSON.stringify(results.data, null, 2) : String(results.data || results.error || '');
    };

    // Pull a readable error string from whatever shape a failed response carries:
    // a flat .error string, or a nested .error.message / {errors:[...]}.
    const extractError = (r) => {
        if (!r) return '';
        let msg = '';
        const candidates = [r.error, r.error?.message, r.message, r.data?.error, r.data?.error?.message, r.data?.message];
        for (let c of candidates) {
            if (!c) continue;
            if (typeof c === 'string') { msg = c; break; }
            if (typeof c === 'object') {
                if (Array.isArray(c.errors)) { msg = c.errors.join('; '); break; }
                if (typeof c.message === 'string') { msg = c.message; break; }
                if (c.message && Array.isArray(c.message.errors)) { msg = c.message.errors.join('; '); break; }
            }
        }
        if (!msg) return '';
        // AT (and most upstreams) return an HTML page for a wrong URL — don't echo markup.
        if (/^\s*<(!doctype|html)/i.test(msg)) {
            const status = r.status || r.data?.status;
            return `${status === 404 ? 'Endpoint not found' : 'Server error'}${status ? ` (${status})` : ''} — the server returned an HTML page, not JSON. Check the URI. See raw response below.`;
        }
        // Cap length so a giant body never fills the banner; full text stays in the <pre>.
        return msg.length > 300 ? msg.slice(0, 300) + '…' : msg;
    };
    const getErrorMessage = () => (results && !results.success) ? extractError(results) : '';

    const modalBody = (
        <div>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                Make a read-only GET request using the connection's credentials. Select source, destination, or test connection.
            </p>

            {/* Connection selector — hidden in standalone mode */}
            {!isStandalone && (
            <div className='d-flex align-items-center gap-2' style={{ marginBottom: 12 }}>
                <span style={{ fontSize: 11, fontWeight: 600 }}>Connection:</span>
                {jobData?.source_connection && (
                    <Button size='sm'
                        color={activeConnection === 'source' ? 'warning' : 'light'}
                        style={{ fontSize: 11, padding: '3px 10px' }}
                        onClick={() => switchConnection('source')}>
                        Source: <span style={{ fontWeight: 600 }}>{jobData.source_connection.service_code || '—'}</span>
                        <span style={{ fontWeight: 400, opacity: 0.65, marginLeft: 4 }}>{jobData.source_connection.name}</span>
                    </Button>
                )}
                {jobData?.destination_connection && (
                    <Button size='sm'
                        color={activeConnection === 'destination' ? 'warning' : 'light'}
                        style={{ fontSize: 11, padding: '3px 10px' }}
                        onClick={() => switchConnection('destination')}>
                        Destination: <span style={{ fontWeight: 600 }}>{jobData.destination_connection.service_code || '—'}</span>
                        <span style={{ fontWeight: 400, opacity: 0.65, marginLeft: 4 }}>{jobData.destination_connection.name}</span>
                    </Button>
                )}
                {jobData?.test_connection && jobData.test_connection.name && (
                    <Button size='sm'
                        color={activeConnection === 'test' ? 'warning' : 'light'}
                        style={{ fontSize: 11, padding: '3px 10px' }}
                        onClick={() => switchConnection('test')}>
                        Test: <span style={{ fontWeight: 600 }}>{jobData.test_connection.service_code || '—'}</span>
                        <span style={{ fontWeight: 400, opacity: 0.65, marginLeft: 4 }}>{jobData.test_connection.name}</span>
                    </Button>
                )}
                <span className='ms-auto d-flex align-items-center gap-1' role='button'
                    style={{ fontSize: 11, color: '#dc3545', cursor: 'pointer' }}
                    onClick={clearCurrentConnection}
                    title='Clear this connection’s inputs and response'>
                    <XCircle size={13} /> Clear
                </span>
            </div>
            )}
            {isStandalone && directConnectionName && (
                <div className='d-flex align-items-center gap-2' style={{ marginBottom: 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 600 }}>Connection:</span>
                    <span style={{ fontSize: 12 }}>
                        <span style={{ fontWeight: 600 }}>{directServiceCode || '—'}</span>
                        <span style={{ opacity: 0.65, marginLeft: 4 }}>{directConnectionName}</span>
                    </span>
                    <span className='ms-auto d-flex align-items-center gap-1' role='button'
                        style={{ fontSize: 11, color: '#dc3545', cursor: 'pointer' }}
                        onClick={clearCurrentConnection}
                        title='Clear inputs and response'>
                        <XCircle size={13} /> Clear
                    </span>
                </div>
            )}

            {/* Quick reference endpoints: pinned items (always shown), then group chips
                that reveal their endpoints below when clicked. */}
            {(() => {
                const { pinned, groups } = getHintLayout();
                if (!pinned.length && !groups.length) return null;

                const endpointChip = (hint, idx) => (
                    <span key={idx} className='api-explorer-chip'
                        style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, cursor: 'pointer' }}
                        onClick={() => pickHint(hint.uri)} title={hint.uri}>
                        {hint.label}
                    </span>
                );
                const active = groups.find(g => g.group === activeGroup);

                return (
                    <div style={{ marginBottom: 12 }}>
                        {pinned.length > 0 && (
                            <div className='d-flex flex-wrap gap-1'>{pinned.map(endpointChip)}</div>
                        )}
                        {groups.length > 0 && (
                            <div className='d-flex flex-wrap gap-1' style={{ marginTop: pinned.length ? 6 : 0 }}>
                                {groups.map((section) => {
                                    const open = section.group === activeGroup;
                                    return (
                                        <span key={section.group}
                                            className={`api-explorer-chip${open ? ' active' : ''}`}
                                            style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                                            onClick={() => toggleGroup(section.group)}>
                                            {open ? <ChevronDown size={11} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                                                : <ChevronRight size={11} style={{ verticalAlign: 'middle', marginRight: 2 }} />}
                                            {section.group}
                                            <span style={{ fontSize: 9, opacity: 0.7, marginLeft: 3 }}>{section.items.length}</span>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                        {active && (
                            <div className='d-flex flex-wrap gap-1' style={{ marginTop: 6 }}>
                                {active.items.map(endpointChip)}
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Autotask: toggle the current entity URI to its metadata (EntityInformation/Fields) endpoint */}
            {isAutotask() && (
                <div className='d-flex align-items-center gap-2' style={{ marginBottom: 12 }}>
                    <div className='form-check form-switch' style={{ margin: 0 }}>
                        <Input
                            type='switch'
                            id='at-entity-info-switch'
                            checked={uriHasEntityInfo(watchedUri)}
                            onChange={toggleEntityInfo}
                        />
                    </div>
                    <span style={{ fontSize: 11 }} onClick={toggleEntityInfo} role='button'>
                        Entity field metadata
                        <span style={{ fontSize: 10, color: '#888', marginLeft: 6 }}>appends {ENTITY_INFO_SUFFIX} to the entity URI</span>
                    </span>
                </div>
            )}

            {/* Composite lookups — configure URI + integrationOptions; user still hits Send. */}
            {getCompositeHints().length > 0 && (
                <div style={{ marginBottom: 12 }}>
                    <div className='d-flex align-items-center gap-2' style={{ marginBottom: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 600 }}>Quick Lookups</span>
                        <span style={{ fontSize: 10, color: '#888' }}>fills the request — then Send</span>
                    </div>
                    <div className='d-flex flex-wrap gap-1'>
                        {getCompositeHints().map((hint, idx) => {
                            const active = watchedUri === hint.uri
                                && rawIntegrationOptions.trim() === (hint.integrationOptions ? JSON.stringify(hint.integrationOptions, null, 2) : '');
                            return (
                                <span key={idx}
                                    className={`api-explorer-chip${active ? ' active' : ''}`}
                                    style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, cursor: 'pointer' }}
                                    onClick={() => applyComposite(hint)}
                                    title={`${hint.uri}${hint.integrationOptions ? ' · ' + JSON.stringify(hint.integrationOptions) : ''}`}>
                                    {hint.label}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Request form */}
            <Form id='dmt-api-explorer-form' onSubmit={handleSubmit(handleExplore)}>
                <Row>
                    <Col md="5">
                        <RegisteredInputsBuilder
                            registrator={register}
                            registratorErrors={errors}
                            inputs={[{
                                label: 'Endpoint',
                                type: 'text',
                                labelSize: 3,
                                inputSize: 9,
                                name: 'exploreUri',
                                placeholder: '/tickets, /companies, /service-tickets, etc.',
                                required: true,
                            }]}
                            />
                    </Col>
                    <Col md="2">
                        <RegisteredInputsBuilder
                            registrator={register}
                            registratorErrors={errors}
                            inputs={[{
                                label: 'Size',
                                type: 'text',
                                labelSize: 4,
                                inputSize: 7,
                                name: 'explorePageSize',
                                placeholder: '5',
                                required: false,
                            }]}
                            />
                    </Col>
                    <Col md="5">
                        <RegisteredInputsBuilder
                            registrator={register}
                            registratorErrors={errors}
                            inputs={[{
                                label: 'Fields',
                                type: 'text',
                                size: 11,
                                labelSize: 2,
                                inputSize: 10,
                                name: 'exploreFields',
                                placeholder: 'id,summary,status,priority',
                                required: false,
                            }]}
                        />
                    </Col>
                </Row>

                {/* Params section with mode toggle */}
                <div className='d-flex align-items-center justify-content-between' style={{ marginBottom: 8 }}>
                    <div className='d-flex align-items-center gap-2'>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>Parameters</span>
                        {argsMode === 'simple' && getParamHints().length > 0 && (
                            <div className='d-flex flex-wrap gap-1'>
                                {getParamHints().map((hint, idx) => (
                                    <span key={idx}
                                        style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, cursor: 'pointer', border: '1px solid #d0e8ff', background: '#f0f7ff', color: '#336' }}
                                        onClick={() => {
                                            // Key already present (e.g. two searchFilter presets) -> swap its value.
                                            const existingIdx = paramRows.findIndex(r => r.key === hint.key);
                                            if (existingIdx >= 0) {
                                                updateParamRow(existingIdx, 'value', hint.value);
                                                return;
                                            }
                                            const emptyIdx = paramRows.findIndex(r => !r.key.trim());
                                            if (emptyIdx >= 0) {
                                                updateParamRow(emptyIdx, 'key', hint.key);
                                                updateParamRow(emptyIdx, 'value', hint.value);
                                            } else {
                                                setParamRows(prev => [...prev, { key: hint.key, value: hint.value }]);
                                            }
                                        }}
                                        title={`${hint.key}: ${hint.value}`}>
                                        +{hint.label}
                                    </span>
                                ))}
                            </div>
                        )}
                        {argsMode === 'simple' && getOperatorReference() && (
                            <>
                                <span id='at-operator-ref' role='button'
                                    style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, cursor: 'pointer', border: '1px dashed #c0c0c0', color: '#666' }}
                                    title='All operators — copy to clipboard'>
                                    operators…
                                </span>
                                <UncontrolledPopover target='at-operator-ref' trigger='legacy' placement='bottom'
                                    style={{ maxWidth: 560 }}>
                                    <PopoverBody style={{ maxHeight: 360, overflow: 'auto', width: 540 }}>
                                        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>{getOperatorReference().label}</div>
                                        <div style={{ fontSize: 9, color: '#888', marginBottom: 8 }}>{getOperatorReference().note}</div>
                                        {getOperatorReference().operators.map((o) => (
                                            <div key={o.op} className='d-flex align-items-start gap-2' style={{ marginBottom: 6 }}>
                                                <Copy size={12} style={{ cursor: 'pointer', flexShrink: 0, marginTop: 2, opacity: 0.6 }}
                                                    title='Copy snippet'
                                                    onClick={() => { handleCopy(o.snippet); ToastBuilder('success', `Copied ${o.op}`); }} />
                                                <div style={{ minWidth: 0 }}>
                                                    <div style={{ fontSize: 10 }}>
                                                        <code style={{ fontWeight: 600 }}>{o.op}</code>
                                                        <span style={{ color: '#888', marginLeft: 6 }}>{o.desc}</span>
                                                    </div>
                                                    <code style={{ fontSize: 9, color: '#555', wordBreak: 'break-all' }}>{o.snippet}</code>
                                                </div>
                                            </div>
                                        ))}
                                    </PopoverBody>
                                </UncontrolledPopover>
                            </>
                        )}
                    </div>
                    <div className='btn-group' style={{ gap: 0 }}>
                        <button type='button' className={`btn btn-sm ${argsMode === 'simple' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            style={{ fontSize: 10, padding: '2px 10px' }} onClick={switchToSimple}>Simple</button>
                        <button type='button' className={`btn btn-sm ${argsMode === 'advanced' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            style={{ fontSize: 10, padding: '2px 10px' }} onClick={switchToAdvanced}>Advanced</button>
                    </div>
                </div>

                {argsMode === 'simple' ? (
                    <div className='api-explorer-params' style={{ borderRadius: 6, padding: 10, marginBottom: 12 }}>
                        {paramRows.map((row, idx) => (
                            <div key={idx} className='d-flex align-items-center gap-2' style={{ marginBottom: idx < paramRows.length - 1 ? 6 : 0 }}>
                                <Input
                                    bsSize='sm'
                                    placeholder='key (e.g. conditions)'
                                    value={row.key}
                                    onChange={(e) => updateParamRow(idx, 'key', e.target.value)}
                                    style={{ flex: '0 0 35%', fontSize: 12 }}
                                />
                                <Input
                                    bsSize='sm'
                                    placeholder='value (e.g. status=Active)'
                                    value={row.value}
                                    onChange={(e) => updateParamRow(idx, 'value', e.target.value)}
                                    style={{ flex: 1, fontSize: 12 }}
                                />
                                <XCircle size={14} style={{ cursor: 'pointer', color: '#dc3545', flexShrink: 0 }} onClick={() => removeParamRow(idx)} />

                            </div>
                        ))}
                        <div className='mt-2'>
                            <span style={{ cursor: 'pointer', fontSize: 11, color: 'var(--theme-default)' }} onClick={addParamRow}>
                                <Plus size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />Add Parameter
                            </span>
                        </div>
                    </div>
                ) : (
                    <div style={{ marginBottom: 12 }}>
                        <Input
                            type='textarea'
                            value={rawArgs}
                            onChange={(e) => setRawArgs(e.target.value)}
                            placeholder='{"conditions": "status=Active", "orderBy": "id desc"}'
                            style={{ fontSize: 12, fontFamily: 'monospace', height: 80 }}
                        />
                    </div>
                )}

                {/* Integration Inputs — integration-specific inputs, separate from query args */}
                <div className='d-flex align-items-center gap-2' style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>Integration Inputs</span>
                    <span style={{ fontSize: 10, color: '#888' }}>integration-specific, not query params</span>
                    {getIntegrationInputHints().length > 0 && (
                        <div className='d-flex flex-wrap gap-1'>
                            {getIntegrationInputHints().map((hint, idx) => (
                                <span key={idx}
                                    style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, cursor: 'pointer', border: '1px solid #ffe0b2', background: '#fff8f0', color: '#663' }}
                                    onClick={() => setRawIntegrationOptions(JSON.stringify(hint.value, null, 2))}
                                    title={JSON.stringify(hint.value)}>
                                    +{hint.label}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div style={{ marginBottom: 12 }}>
                    <Input
                        type='textarea'
                        value={rawIntegrationOptions}
                        onChange={(e) => setRawIntegrationOptions(e.target.value)}
                        placeholder='{"setupField": "status"}  or  {"setupField": "category", "nestedLevel": 2}'
                        style={{ fontSize: 12, fontFamily: 'monospace', height: 60 }}
                    />
                </div>

                <div className='d-flex justify-content-end'>
                    <ButtonBuilder form='dmt-api-explorer-form' label='Send GET Request' className='btn-primary' isProcessing={isProcessing} />
                </div>
            </Form>

            {/* Results */}
            {results && !results.loading && (
                <div style={{ marginTop: 16 }}>
                    <hr />
                    <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 8 }}>
                        <h6 style={{ margin: 0 }}>
                            Response
                            {results.status && (
                                <span className={`ms-2 badge bg-${results.success ? 'success' : 'danger'}`}>
                                    {results.status}
                                </span>
                            )}
                        </h6>
                        <div className='d-flex align-items-center gap-2'>
                            <small className='text-muted'>GET {results.uri}</small>
                            <Copy size={13} style={{ cursor: 'pointer', opacity: 0.5 }} title='Copy response'
                                onClick={() => { handleCopy(getResultString()); ToastBuilder('success', 'Copied'); }} />
                        </div>
                    </div>
                    {!results.success && getErrorMessage() && (
                        <div className='alert alert-danger' style={{ fontSize: 12, padding: '8px 12px', marginBottom: 8 }}>
                            {getErrorMessage()}
                        </div>
                    )}
                    <pre className='api-explorer-result' style={{
                        background: '#1e1e1e',
                        color: '#d4d4d4',
                        padding: 15,
                        borderRadius: 6,
                        maxHeight: 400,
                        overflow: 'auto',
                        fontSize: 12,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    }}>
                        {typeof results.data === 'object'
                            ? JSON.stringify(results.data, null, 2)
                            : results.data || results.error || 'No response data'}
                    </pre>
                    {results.truncated && (
                        <small className='txt-warning'>Response was truncated due to size.</small>
                    )}
                </div>
            )}
        </div>
    );

    if (trigger) {
        return (
            <Fragment>
                <span onClick={openModal} style={{ display: 'inline-flex' }}>{trigger}</span>
                <CommonModal isOpen={modal} title='API Explorer' toggler={() => setModal(false)} size='xl' onSubmit={false} onCancelLabel='Close'>
                    {modalBody}
                </CommonModal>
            </Fragment>
        );
    }

    return (
        <CommonModal isOpen={modal} title='API Explorer' toggler={() => setModal(false)} size='xl' onSubmit={false} onCancelLabel='Close'>
            {modalBody}
        </CommonModal>
    );
};

export default ApiExplorerModal;
